import { useState } from 'react';

/**
 * Pure description of a truncated list view: given the total item count, the
 * current visible `limit` and the `step` size, say whether there's more to
 * show, how many remain, and whether the list is expanded past the first page.
 * Kept pure so it can be unit-tested without a DOM. (QA #4/#5 — WMS-4/WMS-5.)
 */
export function pageView(
  total: number,
  limit: number,
  step: number,
): { hasMore: boolean; remaining: number; expanded: boolean; shown: number } {
  return {
    hasMore: total > limit,
    remaining: Math.max(0, total - limit),
    expanded: limit > step,
    shown: Math.min(Math.max(0, limit), total),
  };
}

/**
 * Truncate a list to `step` rows with show-more / show-less controls. Used by
 * the partner product catalog (10) and the stock-movement list (20).
 */
export function usePaged<T>(items: readonly T[], step = 10) {
  const [limit, setLimit] = useState(step);
  const view = pageView(items.length, limit, step);
  return {
    visible: items.slice(0, limit),
    ...view,
    total: items.length,
    step,
    showMore: () => setLimit((l) => l + step),
    showLess: () => setLimit(step),
  };
}
